import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SubstackHeaderButton, SubstackSlimFooter } from "@/components/SubstackLink";
import { TEAM, BOOKING_URL } from "@/team/teamData";

const fadeUp = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, transition: { duration: 0.7 }, viewport: { once: true } };

const RidgemontLogo = ({ className = "" }) => (
  <svg viewBox="0 0 100 100" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M50 12 L88 82 L12 82 Z" stroke="currentColor" strokeWidth="3" strokeLinejoin="round" />
    <path d="M28 62 L72 62" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

export const TeamPageHeader = () => (
  <header className="bg-white border-b border-[#E7E5E4] sticky top-0 z-40">
    <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
      <a href="/" className="flex items-center gap-3" data-testid="logo-home">
        <RidgemontLogo className="w-8 h-8 text-[#0A192F]" />
        <div className="flex flex-col leading-none">
          <span className="text-xl font-light tracking-tight text-[#0A192F]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Ridgemont</span>
          <span className="text-[10px] tracking-[0.25em] uppercase text-[#C5A059] mt-0.5">Financial</span>
        </div>
      </a>
      <div className="flex items-center gap-3">
        <SubstackHeaderButton />
        <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
          <Button className="btn-accent px-5 py-2 md:px-6 md:py-5 text-sm md:text-base" data-testid="nav-cta">
            Schedule Call
          </Button>
        </a>
      </div>
    </div>
  </header>
);

const TeamPage = () => (
  <div className="bg-white" data-testid="team-page">
    <TeamPageHeader />

    <section className="bg-[#0A192F] text-white py-16 md:py-24">
      <div className="max-w-[1100px] mx-auto px-4 md:px-8 text-center">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <p className="text-sm tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-4">Our Team</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }} data-testid="team-page-headline">
            Meet Your Advisors
          </h1>
          <p className="text-base md:text-lg text-slate-300 max-w-2xl mx-auto">
            Specialists in retirement income, annuities, and capital protection — here to guide you toward a confident retirement.
          </p>
        </motion.div>
      </div>
    </section>

    <section className="py-16 md:py-24">
      <div className="max-w-[1100px] mx-auto px-4 md:px-8 space-y-10">
        {TEAM.map((m, i) => (
          <motion.div {...fadeUp} key={m.slug} className={`grid md:grid-cols-[280px_1fr] gap-8 items-center bg-[#F8F7F4] border border-[#E7E5E4] rounded-2xl p-6 md:p-10 ${i % 2 === 1 ? "md:grid-cols-[1fr_280px]" : ""}`} data-testid={`team-card-${m.slug}`}>
            <div className={`mx-auto w-56 h-56 md:w-64 md:h-64 overflow-hidden rounded-3xl shadow-lg relative ${i % 2 === 1 ? "md:order-2" : ""}`}>
              <img src={m.image} alt={m.name} className="w-full h-full object-cover object-top" />
              <div className="absolute inset-0 rounded-3xl border border-[#C5A059]/30 ring-1 ring-inset ring-white/40" />
            </div>
            <div className={i % 2 === 1 ? "md:order-1" : ""}>
              <h2 className="text-3xl font-light text-[#0A192F] mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{m.name}</h2>
              <p className="text-sm text-[#C5A059] font-medium tracking-wide mb-4 uppercase">{m.title}</p>
              <p className="text-[#57534E] text-sm md:text-base leading-relaxed mb-6">{m.shortBio}</p>
              <div className="flex flex-wrap gap-3">
                <Link to={`/team/${m.slug}`} data-testid={`team-bio-link-${m.slug}`}>
                  <Button variant="outline" className="border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-white">
                    Full Bio <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                  <Button className="btn-accent" data-testid={`team-book-btn-${m.slug}`}>
                    <Calendar className="w-4 h-4 mr-2" /> Book a Call
                  </Button>
                </a>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </section>

    <SubstackSlimFooter />
  </div>
);

export default TeamPage;
